import React from 'react';
import { AlertCircle } from 'lucide-react';
import Button from './Button';

/**
 * Compact error — icon, a short title, at most one short line, one primary
 * action and an optional secondary. `stacked` sits centred in a wait card,
 * `row` replaces a strip or panel, `tiny` fits inside a table cell or chip.
 * Copy is Robbie's voice: what happened, then what to do.
 */
export type CompactErrorLayout = 'stacked' | 'row' | 'tiny';

export interface CompactErrorAction {
  label: string;
  onClick: () => void;
}

export interface CompactErrorProps {
  title: string;
  message?: string;
  layout?: CompactErrorLayout;
  action?: CompactErrorAction;
  secondaryAction?: CompactErrorAction;
  className?: string;
}

const Actions: React.FC<{ action?: CompactErrorAction; secondary?: CompactErrorAction; size: 'sm' | 'md' }> = ({
  action,
  secondary,
  size,
}) =>
  action || secondary ? (
    <div className="flex flex-nowrap items-center justify-center gap-s3">
      {secondary && (
        <Button variant="secondary" size={size} onClick={secondary.onClick}>
          {secondary.label}
        </Button>
      )}
      {action && (
        <Button size={size} onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  ) : null;

export const CompactError: React.FC<CompactErrorProps> = ({
  title,
  message,
  layout = 'stacked',
  action,
  secondaryAction,
  className = '',
}) => {
  if (layout === 'tiny') {
    return (
      <span role="alert" className={`inline-flex items-baseline gap-s1 text-meta ${className}`}>
        <AlertCircle aria-hidden="true" className="h-[13px] w-[13px] shrink-0 self-center text-danger-ink" />
        <span className="font-semibold text-danger-ink">{title}</span>
        {message && <span className="text-ink-secondary">{message}</span>}
        {action && (
          <button type="button" onClick={action.onClick} className="font-semibold text-action hover:underline">
            {action.label}
          </button>
        )}
      </span>
    );
  }
  if (layout === 'row') {
    return (
      <div
        role="alert"
        className={`flex items-center gap-s4 rounded-card border border-danger-border bg-surface px-s5 py-s4 ${className}`}
      >
        <span className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-round bg-danger-bg text-danger-ink">
          <AlertCircle aria-hidden="true" className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-row-title text-ink">{title}</p>
          {message && <p className="text-body-sm text-ink-secondary">{message}</p>}
        </div>
        <Actions action={action} secondary={secondaryAction} size="sm" />
      </div>
    );
  }
  return (
    <div role="alert" className={`flex flex-col items-center text-center ${className}`}>
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-round bg-danger-bg text-danger-ink ring-[6px] ring-danger-bg/50">
        <AlertCircle aria-hidden="true" className="h-5 w-5" />
      </span>
      <p className="mt-s5 text-[16px] font-semibold leading-snug text-ink">{title}</p>
      {message && <p className="mt-s1 text-body-md text-ink-secondary">{message}</p>}
      {(action || secondaryAction) && (
        <div className="mt-s6">
          <Actions action={action} secondary={secondaryAction} size="md" />
        </div>
      )}
    </div>
  );
};

export default CompactError;
